import { Injectable, NgZone, inject } from "@angular/core";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { LogLevel } from "@azure/msal-browser";
import {
    ApplicationInsights,
    IConfig,
    IConfiguration,
    IEventTelemetry,
    IExceptionTelemetry,
    ITelemetryItem,
    ITraceTelemetry
} from "@microsoft/applicationinsights-web";
import { DefaultHeaderService } from "~app/selenium/default-header.service";
import { environment } from "../../../environments/environment";
import { ObjectUtils } from "../utils/object-utils";

type AppInsightsConfig = IConfig & IConfiguration & {
    applicationRole?: string;
    enableConsoleOutput?: boolean;
};

@Injectable({
    providedIn: "root"
})

export class AppInsightsService {
    appInsights: ApplicationInsights;
    authenticatedUserId = "None";
    config: AppInsightsConfig;
    groupError: string[] = [
        "Transition Rejection",
        "400 - Bad Request",
        "503 - Service Unavailable",
        "Possibly unhandled rejection",
        "Duplicates in a repeater"
    ];
    isUserLogIn = false;
    releaseInfoBuildNumber: string;

    private readonly defaultHeaderService = inject(DefaultHeaderService);
    private readonly ngZone = inject(NgZone);
    private readonly router = inject(Router);

    constructor() {
        const ngZone = this.ngZone;

        ngZone.runOutsideAngular(() => {
            this.init();
            this.setTrackPageEvents();
        });
    }

    flush(): void {
        try {
            this.appInsights.flush();
            this.consoleInfo("RASK Application insights [flush]");
        } catch (ex) {
            this.consoleWarn("RASK Application insights Error [flush]: ", ex);
        }
    }

    init(): void {
        this.config = environment.appInsights;
        this.setApplicationVersion(environment.releaseInfo);

        if(!this.config?.instrumentationKey) {
            this.consoleWarn(
                "You need to configure the appInsights section in the environment.ts with the instrumentationKey set at least");
            return;
        }

        try {
            this.appInsights = new ApplicationInsights({
                config: this.config
            });

            this.appInsights.loadAppInsights();

            this.appInsights.addDependencyInitializer(
                (dependencyDetails) => this.isRaskApiCall(dependencyDetails.item.name) ? true : false);

            this.appInsights.addTelemetryInitializer(
                (envelope) => {
                    this.changeExceptionMessage(envelope);
                    envelope.tags["ai.application.ver"] = this.releaseInfoBuildNumber;
                    envelope.tags["ai.device.roleName"] = this.config.applicationRole;

                    if (envelope.data && envelope.data.baseData && envelope.data.baseData.name) {
                        envelope.tags["ai.operation.name"] = envelope.data.baseData.name;
                    }

                    const telemetryItem = envelope.baseData || envelope.data?.baseData;
                    const seleniumCategory = this.getSeleniumTestCategory();
                    if (ObjectUtils.hasValue(seleniumCategory) && envelope.data && envelope.data.baseData) {
                        if (ObjectUtils.hasValue(telemetryItem.properties)) {
                            telemetryItem.properties = telemetryItem.properties || {};
                        }

                        telemetryItem.properties.seleniumCategory = seleniumCategory;
                    }

                    return true;
                });
            this.setAuthenticatedUserContext("None", "None");
        } catch (ex) {
            this.consoleWarn("Angular application insights Error [init]: ", ex);
        }
    }

    onMsalException(logLevel: LogLevel, message: string) {
        switch(logLevel) {
            case LogLevel.Error:
                this.trackEvent({ name: "msalError" }, { Message: message });
                console.error("Msal error", message);
                break;
            case LogLevel.Warning:
                this.trackEvent({ name: "msalWarning" }, { Message: message });
                console.warn("Msal warning", message);
                break;
        }
    }

    onUserLogIn(userName: string, azureUserId: string): void {
        this.ngZone.runOutsideAngular(() => {
            const usrName = this.getUserName(userName);
            this.setAuthenticatedUserContext(usrName, usrName);

            this.trackEvent({ name: "onUserLogIn" }, { User: usrName,
                AzureUserId: azureUserId }, { screenWidth: screen.width,
                screenHeight: screen.height });
            this.isUserLogIn = true;
        });
    }

    onUserLogout(userName: string, azureUserId: string): void {
        this.ngZone.runOutsideAngular(() => {
            const usrName = this.getUserName(userName);
            this.clearAuthenticatedUserContext();

            this.trackEvent({ name: "onUserLogout" } , { User: usrName,
                AzureUserId: azureUserId  });
            this.isUserLogIn = false;
        });
    }

    setTrackPageEvents(): void {
        this.router.events.subscribe(e => {
            if(e instanceof NavigationStart ) {
                this.appInsights.startTrackEvent(e.url);
            } else if(e instanceof NavigationEnd ) {
                this.appInsights.stopTrackEvent(e.url);
                this.appInsights.trackPageView();
            }
        });
    }

    trackTrace(trace: ITraceTelemetry,
        customProperties?: { [name: string]: string }): void {
        this.ngZone.runOutsideAngular(() => {
            try {
                if (ObjectUtils.hasValue(this.appInsights)) {
                    this.appInsights.trackTrace(trace, {
                        properties: {
                            customProperties
                        }
                    });
                }
                this.consoleInfo("RASK Application insights [trackTrace]", trace.message, customProperties);
            } catch (ex) {
                this.consoleWarn("RASK Application insights Error [trackTrace]: ", ex);
            }
        });
    }

    trackEvent(event: IEventTelemetry,
        eventProperties?: { [name: string]: string },
        metricProperty?: { [name: string]: number }): void {
        this.ngZone.runOutsideAngular(() => {
            try {
                if (ObjectUtils.hasValue(this.appInsights)) {
                    this.appInsights.trackEvent(event, {
                        properties: {
                            metricProperty,
                            eventProperties
                        }
                    });
                }
                this.consoleInfo("RASK Application insights [trackEvent]", event.name, eventProperties, metricProperty);
            } catch (ex) {
                this.consoleWarn("RASK Application insights Error [trackEvent]: ", ex);
            }
        });
    }

    trackException(exception: IExceptionTelemetry,
        handledAt?: string,
        properties?: { [name: string]: string },
        measurements?: { [name: string]: number }): void {
        this.ngZone.runOutsideAngular(() => {
            try {
                if (ObjectUtils.hasValue(this.appInsights)) {
                    this.appInsights.trackException(exception, {
                        properties: {
                            measurements,
                            properties,
                            handledAt
                        }
                    });
                }

                console.error(exception);
            } catch (ex) {
                this.consoleWarn("RASK Application insights Error [trackException]: ", ex);
            }
        });
    }

    private changeExceptionMessage(envelope: ITelemetryItem) {
        if (envelope.data && envelope.data.baseType === "ExceptionData") {
            envelope.data.baseData.exceptions.forEach((error: Error) => {
                const existsGroupMsg = this.groupError.find(a => error.message.includes(a));
                if (existsGroupMsg) {
                    error.message = existsGroupMsg;
                }
            });
        }
    }

    private clearAuthenticatedUserContext(): void {
        this.authenticatedUserId = "None";
        try {
            if (ObjectUtils.hasValue(this.appInsights)) {
                this.appInsights.clearAuthenticatedUserContext();
            }
            this.consoleInfo("Angular application insights [clearAuthenticatedUserContext]");
        } catch (ex) {
            this.consoleWarn("Angular application insights Error [clearAuthenticatedUserContext]: ", ex);
        }
    }

    private consoleInfo(message?: any, ...optionalParams: any[]): void {
        if (this.config && this.config.enableConsoleOutput) {
            // eslint-disable-next-line
            console.info(message, optionalParams);
        }
    }

    private consoleWarn(message?: any, ...optionalParams: any[]): void {
        console.warn(message, optionalParams);
    }

    private getSeleniumTestCategory(): string {
        return this.defaultHeaderService.headers["rask-selenium-test-category"];
    }

    private getUserName(userName: string): string {
        const usrName = ObjectUtils.hasValue(userName)
            ? userName
            : "";
        return usrName.replace(/[,;=| ]+/g, "_");
    }

    private isRaskApiCall(path: string): boolean {
        if (path) {
            return (path.includes(environment.api) || path.includes(environment.apiV2));
        }

        return false;
    }

    private setApplicationVersion(releaseInfo: any): void {
        if (releaseInfo && releaseInfo.buildNumber) {
            this.releaseInfoBuildNumber = releaseInfo.buildNumber;
        } else {
            // eslint-disable-next-line sonarjs/no-hardcoded-ip
            this.releaseInfoBuildNumber = "1.0.0.0";
        }
    }

    private setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string): void {
        try {
            this.authenticatedUserId = authenticatedUserId;
            if (ObjectUtils.hasValue(this.appInsights)) {
                this.appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId);
                this.appInsights.context.user.id = authenticatedUserId;
            }
            this.consoleInfo("Angular application insights [setAuthenticatedUserContext]",
                authenticatedUserId,
                accountId);
        } catch (ex) {
            this.consoleWarn("Angular application insights Error [setAuthenticatedUserContext]: ", ex);
        }
    }
}
